import { FunctionComponent, useEffect, useRef } from 'react';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import { parseHtml } from '../Helper';
import ColorSchemes from '../../Data/ColorSchemes.json';
import { LoadingIndicator } from '../../components/LoadingIndicator';
import { RatingScaleLabel } from '../RatingScaleLabel';
import type { LegacyResponse, Template } from '../../types';

interface RatingPageProps {
  notRated?: boolean;
  rating?: number | string | null;
  ratings?: number[];
  template: Template;
  rate: (rating: number) => void;
  sending?: boolean;
  setIsRatingPageRendered: (value: boolean) => void;
  response?: LegacyResponse;
}

const HEX_COLOR_REGEX = /^#(?:[0-9a-f]{3}){1,2}$/i;
const RGB_COLOR_REGEX = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i;

function isValidColor(color?: string): boolean {
  if (!color) {
    return false;
  }
  if (HEX_COLOR_REGEX.test(color)) {
    return true;
  }

  const match = RGB_COLOR_REGEX.exec(color);
  if (!match) {
    return false;
  }

  const [r, g, b] = match.slice(1).map(Number);
  return [r, g, b].every((value) => value >= 0 && value <= 255);
}

function handleRatingBoxesColor(rating: number, template: Template): string | false | undefined {
  if (template.htmlTemplateId === 1) {
    return (
      (rating < 7 && ColorSchemes.DETRACTOR) ||
      ((rating === 7 || rating === 8) && ColorSchemes.PASSIVE) ||
      (rating > 8 && ColorSchemes.PROMOTER)
    );
  }
  const isColorValid = isValidColor(template.colorScheme);

  if (!isColorValid) {
    return getColorBasedOnTdBgColor(template.colorScheme);
  }

  return template.colorScheme;
}

function getColorBasedOnTdBgColor(color?: string): string {
  const tempTd = document.createElement('td');

  tempTd.setAttribute('bgcolor', color ?? '');
  tempTd.style.display = 'none';

  document.body.appendChild(tempTd);

  const styles = globalThis.getComputedStyle(tempTd);
  const bgColor = styles.backgroundColor;

  tempTd.remove();

  return bgColor;
}

export const RatingPage: FunctionComponent<RatingPageProps> = ({
  notRated,
  rating,
  ratings,
  template,
  rate,
  sending,
  setIsRatingPageRendered,
  response
}) => {
  const appInsights = useAppInsightsContext();
  const setIsRatingPageRenderedRef = useRef(setIsRatingPageRendered);
  useEffect(() => {
    const setIsRendered = setIsRatingPageRenderedRef.current;
    setIsRendered(true);
    appInsights.trackPageView({ name: 'RatingPage.tsx' });
    return () => {
      setIsRendered(false);
    };
  }, [appInsights]);
  return (
    <div className="rating-container body">
      <div className="question-container">
        <h2 className="question">{parseHtml(template.content)}</h2>
      </div>
      <br />
      <div className="rating">
        {ratings?.map((ratingValue: number) => {
          return (
            <a
              href="#/"
              key={ratingValue}
              onClick={() => {
                if (!sending) {
                  rate(ratingValue);
                  appInsights.trackEvent({
                    name: 'Rated',
                    properties: response
                  });
                }
              }}
            >
              <div
                className="button-link"
                style={
                  rating === ratingValue
                    ? {
                        backgroundColor: '',
                        borderColor: handleRatingBoxesColor(ratingValue, template) || undefined
                      }
                    : {
                        backgroundColor: handleRatingBoxesColor(ratingValue, template) || undefined
                      }
                }
              >
                <p
                  className="rating-figure"
                  style={
                    rating === ratingValue
                      ? {
                          color: handleRatingBoxesColor(ratingValue, template) || undefined
                        }
                      : {}
                  }
                >
                  {ratingValue}
                </p>
                {rating === ratingValue && sending && notRated && (
                  <div
                    className="loading-indicator-wrapper"
                    style={{
                      color: handleRatingBoxesColor(ratingValue, template) || undefined
                    }}
                  >
                    <LoadingIndicator />
                  </div>
                )}
              </div>
            </a>
          );
        })}
      </div>
      <div className="likeliness clearfix">
        <RatingScaleLabel
          end="min"
          text={template.lowRatingText}
          showSmiley={template.enableSmiley}
        />
        <RatingScaleLabel
          end="max"
          text={template.highRatingText}
          showSmiley={template.enableSmiley}
        />
      </div>
    </div>
  );
};
